import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  linkWithPopup,
  linkWithRedirect,
  linkWithCredential,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  reauthenticateWithRedirect,
  getRedirectResult,
  deleteUser,
  sendEmailVerification,
  GoogleAuthProvider,
  EmailAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously,
  sendPasswordResetEmail,
  updateProfile as updateAuthProfile,
  signOut,
} from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase";
import { ensureProfile, eraseProfile, heartbeat, randomAvatar, cleanName } from "../lib/profile";
import { TV_AVATAR, TV_NAME, isTvTab } from "../lib/tvTab";
import { forgetRoom } from "../lib/rejoin";
import { checkPassword } from "../lib/password";
import { levelInfo, XP_PER_MIN_LOBBY, XP_PER_MIN_PLAYING, XP_TICK_MS, XP_MAX_PER_TICK } from "../lib/xp";
import { useToast } from "./ToastContext";
import { mark, tt, useI18n } from "../i18n";

const AuthContext = createContext(null);

const GUEST_KEY = "partycade.guest";
// In dev "?solo" mode each tab is its own player, so keep the guest per tab too.
const guestStore = () => {
  try {
    return sessionStorage.getItem("partycade.solo") ? sessionStorage : localStorage;
  } catch {
    return localStorage;
  }
};

function loadGuest() {
  try {
    const g = JSON.parse(guestStore().getItem(GUEST_KEY));
    if (g && g.avatar) return g;
  } catch {}
  // A TV tab is simply called "TV": it never asks for a nickname.
  if (isTvTab()) return { name: TV_NAME, avatar: TV_AVATAR };
  return { name: "", avatar: randomAvatar() };
}

const AUTH_ERRORS = {
  "auth/email-already-in-use": mark("That email already has an account - try signing in."),
  "auth/invalid-email": mark("That email doesn't look right."),
  "auth/invalid-credential": mark("Wrong email or password."),
  "auth/wrong-password": mark("Wrong email or password."),
  "auth/user-not-found": mark("No account with that email."),
  "auth/too-many-requests": mark("Too many attempts. Take a breather and try again soon."),
  "auth/popup-closed-by-user": mark("Sign-in popup was closed."),
  "auth/popup-blocked": mark("Your browser blocked the popup - allow popups and retry."),
  "auth/network-request-failed": mark("Network hiccup. Check your connection."),
  "auth/operation-not-allowed": mark("This sign-in method isn't enabled in Firebase yet."),
  "auth/admin-restricted-operation": mark("Guest play isn't enabled in Firebase yet (turn on Anonymous auth)."),
  "auth/password-does-not-meet-requirements": mark("Password doesn't meet the requirements."),
  "auth/provider-already-linked": mark("You're already signed in with that."),
};
export const authErrorMessage = (e) => (AUTH_ERRORS[e?.code] ? tt(AUTH_ERRORS[e.code]) : e?.message || tt("Something went wrong."));

// Phones and tablets go to Google in the same tab instead of a popup: some
// phone browsers open the popup as a separate tab that can't hand the result
// back ("Unable to process request due to missing initial state"). The
// same-tab way works because sign-in runs through our own domain (authDomain
// in firebase.js). What we were doing is remembered for when we come back.
const REDIRECT_KEY = "partycade.googleRedirect";
const REDIRECT_MAX_AGE = 10 * 60_000;
export function sameTabSignIn() {
  try {
    return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || window.matchMedia("(pointer: coarse)").matches;
  } catch {
    return false;
  }
}
function rememberRedirect(what) {
  try {
    sessionStorage.setItem(REDIRECT_KEY, JSON.stringify({ ...what, at: Date.now() }));
  } catch {}
}
function takeRedirect() {
  try {
    const what = JSON.parse(sessionStorage.getItem(REDIRECT_KEY));
    sessionStorage.removeItem(REDIRECT_KEY);
    return what && Date.now() - what.at < REDIRECT_MAX_AGE ? what : null;
  } catch {
    return null;
  }
}

// Deletes the signed-in account's data and login (after a fresh sign-in).
async function eraseAccount(u) {
  await eraseProfile(u.uid);
  await deleteUser(u);
  forgetRoom();
}

export function AuthProvider({ children }) {
  const { toast } = useToast();
  // Firebase's own emails (verify, reset password) follow the site's language.
  const { lang } = useI18n();
  useEffect(() => {
    auth.languageCode = lang;
  }, [lang]);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guest, setGuestState] = useState(loadGuest);
  const [authModal, setAuthModal] = useState(null); // null | "signin" | "signup"
  const pendingName = useRef("");
  const activity = useRef(null); // { roomCode, game, playing }
  const bonusXp = useRef(0);
  const lastLevel = useRef(null);

  const syncUser = useCallback(async (u) => {
    if (u && !u.isAnonymous) {
      try {
        await ensureProfile(u, pendingName.current);
      } catch (e) {
        console.error("Profile setup failed", e);
        toast({ emoji: "😵", kind: "error", title: tt("Couldn't set up your profile"), body: e.message, duration: 8000 });
      }
    }
    // Copy the fields we use so a guest -> account upgrade (same object) re-renders.
    setUser(
      u
        ? {
            uid: u.uid,
            isAnonymous: u.isAnonymous,
            email: u.email,
            displayName: u.displayName,
            providers: u.providerData.map((p) => p.providerId),
            emailVerified: u.emailVerified,
          }
        : null
    );
    setLoading(false);
  }, [toast]);

  useEffect(() => onAuthStateChanged(auth, syncUser), [syncUser]);

  // Live profile document.
  useEffect(() => {
    if (!user || user.isAnonymous) {
      setProfile(null);
      lastLevel.current = null;
      return;
    }
    return onSnapshot(
      doc(db, "users", user.uid),
      (snap) => setProfile(snap.exists() ? { uid: user.uid, ...snap.data() } : null),
      (e) => console.error(e)
    );
  }, [user]);

  // Level-up celebration.
  useEffect(() => {
    if (!profile) return;
    const { level } = levelInfo(profile.xp);
    if (lastLevel.current && level > lastLevel.current) {
      toast({ emoji: "🎉", kind: "success", title: tt("Level up! You're now level {level}", { level }), duration: 6000 });
    }
    lastLevel.current = level;
  }, [profile, toast]);

  // Heartbeat + time-based XP. Only counts while the tab is visible.
  const profileRef = useRef(profile);
  profileRef.current = profile;
  useEffect(() => {
    if (!user || user.isAnonymous) return;
    const beat = () => {
      const p = profileRef.current;
      if (!p) return;
      const a = activity.current;
      const visible = document.visibilityState === "visible";
      let gain = 0;
      if (a && visible) gain = a.playing ? XP_PER_MIN_PLAYING : XP_PER_MIN_LOBBY;
      const lastXp = p.lastXpAt?.toMillis?.() ?? 0;
      // The security rules reject XP writes closer than 50s apart.
      if (Date.now() - lastXp < XP_TICK_MS - 5000) gain = 0;
      if (gain > 0 && bonusXp.current > 0) {
        const extra = Math.min(bonusXp.current, XP_MAX_PER_TICK - gain);
        gain += extra;
        bonusXp.current -= extra;
      }
      const status = a ? { roomCode: a.roomCode, game: a.game, playing: !!a.playing } : null;
      heartbeat(user.uid, gain, status).catch((e) => console.warn("heartbeat failed", e));
    };
    const first = setTimeout(beat, 3000);
    const id = setInterval(beat, XP_TICK_MS);
    return () => {
      clearTimeout(first);
      clearInterval(id);
    };
  }, [user]);

  const setActivity = useCallback((a) => {
    activity.current = a;
  }, []);
  const addXpBonus = useCallback((n) => {
    bonusXp.current += n;
  }, []);

  const setGuest = useCallback((g) => {
    setGuestState((prev) => {
      const next = { ...prev, ...g };
      try {
        guestStore().setItem(GUEST_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, []);

  // ---- Auth actions ----
  const signInGoogle = useCallback(async () => {
    const cur = auth.currentUser;
    if (sameTabSignIn()) {
      // The page goes to Google and comes back (see the effect below).
      rememberRedirect({ kind: "signin" });
      if (cur?.isAnonymous) await linkWithRedirect(cur, googleProvider);
      else await signInWithRedirect(auth, googleProvider);
      return;
    }
    if (cur?.isAnonymous) {
      // Upgrade the guest in place so they keep their seat in any room.
      try {
        const res = await linkWithPopup(cur, googleProvider);
        await syncUser(res.user);
        return;
      } catch (e) {
        if (e.code !== "auth/credential-already-in-use") throw e;
        const cred = GoogleAuthProvider.credentialFromError(e);
        if (cred) {
          await signInWithCredential(auth, cred);
          return;
        }
      }
    }
    await signInWithPopup(auth, googleProvider);
  }, [syncUser]);

  // Email sign-ups must confirm their address before using the account
  // (Google accounts are already verified by Google).
  const sendVerification = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) return;
    try {
      // "Continue" on the confirmation page brings them back to the site.
      await sendEmailVerification(u, { url: `${window.location.origin}/` });
    } catch (e) {
      if (e.code !== "auth/unauthorized-continue-uri" && e.code !== "auth/invalid-continue-uri") throw e;
      await sendEmailVerification(u);
    }
  }, []);

  // Asks Firebase whether the link in the email has been clicked yet.
  // The link may also have been opened in another tab of this site: then
  // Firebase has already marked this tab's user verified, and only the
  // screen needs updating.
  const checkVerification = useCallback(async ({ quiet = false } = {}) => {
    const u = auth.currentUser;
    if (!u) return false;
    if (!u.emailVerified) {
      await u.reload();
      if (!u.emailVerified) return false;
      await u.getIdToken(true);
    }
    await syncUser(u);
    if (!quiet) toast({ emoji: "✅", kind: "success", title: tt("Email verified!"), body: tt("Welcome to PartyCade.") });
    return true;
  }, [syncUser, toast]);

  const signUpEmail = useCallback(
    async (name, email, password) => {
      if (!checkPassword(password).valid) throw new Error(tt("Password doesn't meet the requirements."));
      const displayName = cleanName(name);
      if (displayName.length < 2) throw new Error(tt("Pick a name with at least 2 characters."));
      pendingName.current = displayName;
      const cur = auth.currentUser;
      let u;
      if (cur?.isAnonymous) {
        u = (await linkWithCredential(cur, EmailAuthProvider.credential(email, password))).user;
      } else {
        u = (await createUserWithEmailAndPassword(auth, email, password)).user;
      }
      await updateAuthProfile(u, { displayName });
      await syncUser(u);
      await sendVerification().catch((e) => console.warn("Couldn't send the verification email", e));
    },
    [syncUser, sendVerification]
  );

  const signInEmail = useCallback((email, password) => signInWithEmailAndPassword(auth, email, password), []);
  const resetPassword = useCallback((email) => sendPasswordResetEmail(auth, email), []);

  const playAsGuest = useCallback(
    async (name, avatar) => {
      setGuest({ name: cleanName(name), ...(avatar ? { avatar } : {}) });
      if (!auth.currentUser) await signInAnonymously(auth);
    },
    [setGuest]
  );

  const logout = useCallback(() => signOut(auth), []);

  // Deletes the signed-in account for good. Firebase wants a fresh sign-in
  // first: the password for email accounts, Google otherwise. Returns true
  // once deleted, or false when a phone went to Google to confirm (the
  // deletion then finishes when it comes back, see below).
  const deleteAccount = useCallback(async (password) => {
    const u = auth.currentUser;
    if (!u || u.isAnonymous) return false;
    if (password) await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, password));
    else if (sameTabSignIn()) {
      rememberRedirect({ kind: "delete", uid: u.uid });
      await reauthenticateWithRedirect(u, googleProvider);
      return false;
    } else await reauthenticateWithPopup(u, googleProvider);
    await eraseAccount(u);
    return true;
  }, []);

  // Back from Google in the same tab: finish what we went there for.
  useEffect(() => {
    const pending = takeRedirect();
    if (!pending) return;
    getRedirectResult(auth)
      .then(async (res) => {
        if (!res) return;
        if (pending.kind === "delete") {
          if (res.operationType !== "reauthenticate" || res.user.uid !== pending.uid) return;
          await eraseAccount(res.user);
          toast({ emoji: "👋", title: tt("Your account was deleted"), body: tt("Thanks for playing. You can still play as a guest.") });
          return;
        }
        await syncUser(res.user);
      })
      .catch(async (e) => {
        console.warn("Google sign-in (same tab) failed", e);
        // A guest picked a Google account that already has a PartyCade account: sign in to it.
        if (e.code === "auth/credential-already-in-use") {
          const cred = GoogleAuthProvider.credentialFromError(e);
          if (cred) {
            await signInWithCredential(auth, cred).catch(() => {});
            return;
          }
        }
        toast({ emoji: "😵", kind: "error", title: tt("Couldn't sign in with Google"), body: authErrorMessage(e), duration: 8000 });
      });
  }, [syncUser, toast]);

  const isGuest = !!user?.isAnonymous;
  const needsVerification = !!user && !isGuest && !!user.providers?.includes("password") && !user.emailVerified;

  const playerInfo = useMemo(() => {
    if (profile) {
      return {
        name: profile.displayName,
        avatar: profile.avatar,
        isGuest: false,
        friendCode: profile.friendCode,
        level: levelInfo(profile.xp).level,
      };
    }
    return { name: guest.name || "Guest", avatar: guest.avatar, isGuest: true, friendCode: null, level: null };
  }, [profile, guest]);

  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      // Signed-in accounts need their Firestore profile before joining rooms.
      profileReady: !user || isGuest || !!profile,
      isGuest,
      needsVerification,
      sendVerification,
      checkVerification,
      guest,
      setGuest,
      playerInfo,
      signInGoogle,
      signUpEmail,
      signInEmail,
      resetPassword,
      playAsGuest,
      logout,
      deleteAccount,
      setActivity,
      addXpBonus,
      authModal,
      openAuth: (mode = "signin") => setAuthModal(mode),
      closeAuth: () => setAuthModal(null),
    }),
    [
      user,
      profile,
      loading,
      isGuest,
      needsVerification,
      sendVerification,
      checkVerification,
      guest,
      setGuest,
      playerInfo,
      signInGoogle,
      signUpEmail,
      signInEmail,
      resetPassword,
      playAsGuest,
      logout,
      deleteAccount,
      setActivity,
      addXpBonus,
      authModal,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
